import * as API from 'patientOnboarding/api/legacy';
import * as API_V1 from 'patientOnboarding/api/v2/obie';
import * as Effects from 'redux-saga/effects';
import { PayloadAction, PrepareAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { all, put, takeLatest } from 'redux-saga/effects';
import {
  handleZipCodeValidationSuccess,
  handleZipCodeValidationClear,
  shouldUpdateAppStateCookie,
} from 'patientOnboarding/utils/zipCodeIntegration';

import {
  ZipCodePayload,
  setCliniciansAvailableOnZipCode,
  setErrors,
  setLoading,
  setLoadingCountByZip,
  setValidZipCode,
  updateInsuranceList,
  updatePreferences,
} from 'patientOnboarding/store/preferences/actions';

import { setIsEnabled, setIsStateEnabled } from '../admin/reducers';
import t from './types';

const call: any = Effects.call;

function* handleFetchZipCode(action: PayloadAction<string>): any {
  try {
    yield put(setValidZipCode(false));
    yield put(setErrors({ zipCode: '' }));
    const response = yield call(API_V1.validateZipCode, action.payload);
    const insuranceCompanies = response.data.insurances.map((insurance: Array<string>) => ({
      label: insurance,
      value: insurance,
    }));
    const typesOfCare = response.data.type_of_cares.map((typeOfCare: Array<string>) => ({
      label: typeOfCare,
      value: typeOfCare,
    }));

    if (response) {
      yield put(setIsEnabled(!!response.data.enabled_obie));
      yield put(setIsStateEnabled(!!response.data.lifestance_state));
      yield put(setValidZipCode(true));
      yield put(
        updatePreferences({
          filters: {
            insuranceCompanies,
            typesOfCare,
          },
        }),
      );

      // Handle app_state cookie for load balancer redirection
      if (shouldUpdateAppStateCookie(response.data, action.payload)) {
        handleZipCodeValidationSuccess(response.data, String(action.payload));
      }
    }
  } catch (e: any) {
    if (e instanceof AxiosError) {
      yield put(setValidZipCode(false));
      e as AxiosError;
      yield put(setErrors({ zipCode: e.response?.data.message }));
      yield put(
        updatePreferences({
          filters: {
            insuranceCompanies: [],
            typesOfCare: [],
          },
        }),
      );
    }
  }
}

function* handleValidateZipCode(action: PayloadAction<ZipCodePayload>): any {
  yield put(setLoading(true));
  yield put(setValidZipCode(false));
  try {
    const isZipCodeValid = yield call(API_V1.validateZipCode, action.payload.zipCode);
    const { city, state, enabled_obie, lifestance_state } = isZipCodeValid.data;
    const insuranceCompanies = isZipCodeValid.data.insurances.map((insurance: Array<string>) => ({
      label: insurance,
      value: insurance,
    }));
    const typesOfCare = isZipCodeValid.data.type_of_cares.map((typeOfCare: Array<string>) => ({
      label: typeOfCare,
      value: typeOfCare,
    }));
    yield put(setLoading(false));

    if (isZipCodeValid) {
      yield put(setIsEnabled(!!enabled_obie));
      yield put(setIsStateEnabled(!!lifestance_state));
      yield put(setValidZipCode(true));
      yield put(updatePreferences({ city }));
      yield put(updatePreferences({ state }));
      if (action.payload.setState) {
        yield put(updatePreferences({ displayedState: state }));
      }
      yield put(
        updatePreferences({
          filters: {
            insuranceCompanies,
            typesOfCare,
          },
        }),
      );

      // Handle app_state cookie for load balancer redirection
      if (shouldUpdateAppStateCookie({ state, stateCode: state }, String(action.payload.zipCode))) {
        handleZipCodeValidationSuccess({ state, stateCode: state }, String(action.payload.zipCode));
      }
    }
  } catch (e: any) {
    const response = e?.response?.data;
    yield put(setIsEnabled(response?.enabled_obie));
    yield put(setIsStateEnabled(response?.lifestance_state));
    yield put(updatePreferences({ state: response?.state }));
    yield put(setLoading(false));
    yield put(setValidZipCode(false));
    if (response.message === 'Enter a valid zip code for United States') {
      yield put(setErrors({ zipCode: response.message }));
    }
  }
}

function* getInsuranceList(action: any): any {
  try {
    const response = yield call(API.getInsuranceList, action.payload);
    const insuranceCompanies = response?.data?.insurances.map((insurance: Array<string>) => ({
      label: insurance,
      value: insurance,
    }));
    yield put(
      updateInsuranceList({
        insuranceCompanies,
      }),
    );
  } catch (e) {
    yield put(setErrors({ insuranceList: 'Could not fetch data' }));
  }
}

function* getClinicianCountByZipCode(action: PayloadAction<ZipCodePayload>): any {
  try {
    yield put(setLoadingCountByZip(true));
    const response = yield call(API_V1.getClinicianCountByZipCode, action.payload.zipCode);
    const licensedCliniciansOnZip = response.data.clinician_count;
    const state = response.data.state;
    yield put(setCliniciansAvailableOnZipCode(licensedCliniciansOnZip));
    yield put(updatePreferences({ state }));
    if (action?.payload?.setState) {
      yield put(updatePreferences({ displayedState: state }));
    }
    yield put(setValidZipCode(true));
    yield put(setLoadingCountByZip(false));
  } catch (e) {
    yield put(setLoadingCountByZip(false));
    if (e instanceof AxiosError) {
      yield put(setValidZipCode(false));
      yield put(setCliniciansAvailableOnZipCode(0));
      yield put(setErrors({ zipCode: 'Enter a valid zip code for United States' }));

      // Clear app_state cookie when zip code validation fails
      handleZipCodeValidationClear();
    }
  }
}

function* getCliniciansValidate(action: PayloadAction<string>): any {
  try {
    yield put(setValidZipCode(false));
    yield call(API_V1.getClinicianCountByZipCode, action.payload);
    yield put(setValidZipCode(true));
  } catch (e) {
    if (e instanceof AxiosError) {
      yield put(setValidZipCode(false));
      yield put(setErrors({ zipCode: 'Enter a valid zip code for United States' }));

      // Clear app_state cookie when zip code validation fails
      handleZipCodeValidationClear();
    }
  }
}

function* preferencesSaga(): any {
  yield all([
    yield takeLatest(t.VALIDATE_ZIPCODE, handleValidateZipCode),
    yield takeLatest(t.FETCH_ZIP_CODE, handleFetchZipCode),
    yield takeLatest(t.GET_INSURANCE_LIST, getInsuranceList),
    yield takeLatest(t.GET_CLINICIANS_COUNT, getClinicianCountByZipCode),
    yield takeLatest(t.GET_CLINICIANS_VALID, getCliniciansValidate),
  ]);
}

export default preferencesSaga;
